import { createFileRoute, getRouteApi, useRouter } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { settingsService } from '@/services/settings';
import { useToast } from '@/hooks/useToast';
import { showFlashMessage } from '@/utils/flashMessageUtils';
import EmailPreferences from '@/components/Settings/EmailPreferences';
import type { EmailPreferences as EmailPreferencesType } from '@/types/settings';

const settingsRoute = getRouteApi('/_app/settings');

export const Route = createFileRoute('/_app/settings/email')({
  component: EmailPreferencesPage,
});

function EmailPreferencesPage() {
  const router = useRouter();
  const settings = settingsRoute.useLoaderData();
  const { showToast } = useToast();
  const formKey = settings.user.formKey;

  // Email preferences mutation
  const updateEmailPreferencesMutation = useMutation({
    mutationFn: ({
      preferences,
      formKey,
    }: {
      preferences: EmailPreferencesType;
      formKey: string;
    }) => settingsService.updateEmailPreferences(preferences, formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  const handlePreferenceChange = (field: keyof EmailPreferencesType, value: boolean) => {
    // Get current preferences from loader data
    const updatedPreferences = {
      ...settings.emailPreferences,
      [field]: value,
    };

    // Submit mutation
    updateEmailPreferencesMutation.mutate({
      preferences: updatedPreferences,
      formKey,
    });
  };

  return (
    <EmailPreferences
      preferences={settings.emailPreferences}
      formKey={formKey}
      onPreferenceChange={handlePreferenceChange}
      isUpdating={updateEmailPreferencesMutation.isPending}
    />
  );
}
